import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { clone } from '../utility';

export const typedMemo: <T>(c: T, propsAreEqual?: Parameters<typeof React.memo>[1]) => T = React.memo;

interface SelectPickerProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
	selectpicker?: Partial<BootstrapSelectOptions>;
}
export const SelectPicker = typedMemo(({selectpicker, ...props}: SelectPickerProps) => {
	const selectRef = useRef(null);

	useEffect(() => {
		$(selectRef.current! as HTMLSelectElement).selectpicker(selectpicker);
	}, []);

	const onChange = () => {
		if (props.onChange && selectRef.current) props.onChange($(selectRef.current!).val());
	}
	
	return (
		<select {...props} ref={selectRef} onChange={onChange}>
			{props.children}
		</select>
	);
});

interface ModalProps {
	id?: string,
	title?: string,
	size?: BootstrapSize,
	show?: boolean,
	centered?: boolean,
	scrollable?: boolean,
	onShow?(e: JQuery.TriggeredEvent): void,
	onShown?(e: JQuery.TriggeredEvent): void,
	onHide?(e: JQuery.TriggeredEvent): void,
	onHidden?(e: JQuery.TriggeredEvent): void,
	children?: React.ReactNode,
}
export const Modal = ({id = undefined, title = '', size = 'lg', centered = true, scrollable = false, show = false, onShow, onShown, onHide, onHidden, children}: ModalProps) => {
	const modalRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!modalRef.current) return;
		const $modal = $(modalRef.current);
		$modal.off('show.bs.modal');
		if (onShow) $modal.on('show.bs.modal', onShow);
	}, [onShow]);

	useEffect(() => {
		if (!modalRef.current) return;
		const $modal = $(modalRef.current);
		$modal.off('shown.bs.modal');
		if (onShown) $modal.on('shown.bs.modal', onShown);
	}, [onShown]);

	useEffect(() => {
		if (!modalRef.current) return;
		const $modal = $(modalRef.current);
		$modal.off('hide.bs.modal');
		if (onHide) $modal.on('hide.bs.modal', onHide);
	}, [onHide]);

	useEffect(() => {
		if (!modalRef.current) return;
		const $modal = $(modalRef.current);
		$modal.off('hidden.bs.modal');
		if (onHidden) $modal.on('hidden.bs.modal', onHidden);
	}, [onHidden]);
	
	useEffect(() => {
		if (!modalRef.current) return;
		const $modal = $(modalRef.current);
		$modal.modal(show ? 'show' : 'hide');
	}, [show]);

	return (
		<div ref={modalRef} className="modal fade" id={id} tabIndex={-1} role="dialog" data-backdrop="static">
			<div className={`modal-dialog ${centered ? 'modal-dialog-centered' : ''} ${scrollable ? 'modal-dialog-scrollable' : ''} modal-${size}`} role="document">
				<div className="modal-content">
					<div className="modal-header">
						<h5 className="modal-title">{title}</h5>
						<button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
					</div>
					{children}
				</div>
			</div>
		</div>
	);
};


interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
	datetimepicker?: any;
	datepicker?: any;
	onPickerChange?: (date: string | null) => void;
}

export const Input = ({ datetimepicker, datepicker, onPickerChange, ...props }: InputProps) => {
	const inputRef = useRef<HTMLInputElement>(null);

	const isDateOnly = Boolean(datepicker);
	const isDateTime = Boolean(datetimepicker);

	useEffect(() => {
		if (!isDateTime || !inputRef.current) return;
		const $input = $(inputRef.current);
		$input.attr('type', 'text').datetimepicker({ theme: 'dark', format: 'm/d/Y g:i A', formatTime: 'g:i A', validateOnBlur: false, ...(typeof datetimepicker === 'object' ? datetimepicker : {}) });

		return () => {
			$input.datetimepicker('destroy');
		};
	}, []);

	useEffect(() => {
		if (!isDateOnly || !inputRef.current) return;
		const $input = $(inputRef.current);
		$input.attr('type', 'text').datetimepicker({ theme: 'dark', format: 'm/d/Y', formatDate: 'm/d/Y', timepicker: false, ...(typeof datepicker === 'object' ? datepicker : {}) });

		return () => {
			$input.datetimepicker('destroy');
		};
	}, []);

	useEffect(() => {
		if (!isDateTime || !inputRef.current || !onPickerChange) return;
		const $input = $(inputRef.current);
		const handleChange = () => {

			const value = $input.val();
			if (!value) {
				onPickerChange(null);
				return;
			}
			const parsed = moment(value, ['MM/DD/YYYY h:mm A', 'MM/DD/YY h:mm A'], true);

			onPickerChange(parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : null);
		};

		$input.on('change.hubDateTimePicker', handleChange);

		return () => {
			$input.off('change.hubDateTimePicker', handleChange);
		};
	}, [isDateTime, onPickerChange]);

	useEffect(() => {
		if (!isDateOnly || !inputRef.current || !onPickerChange) return;

		const $input = $(inputRef.current);
		const handleChange = () => {
			const value = $input.val();

			if (!value) {
				onPickerChange(null);
				return;
			}

			const parsed = moment(value, 'MM/DD/YYYY', true);

			onPickerChange( parsed.isValid() ? parsed.format('YYYY-MM-DD') : null);
		};
		$input.on('change.hubDatePicker', handleChange);

		return () => {
			$input.off('change.hubDatePicker', handleChange);
		};
	}, [isDateOnly, onPickerChange]);

	// Synchronize the date/time input without changing date-only behavior.
	useEffect(() => {
		if (!isDateTime || !inputRef.current) return;
		const $input = $(inputRef.current);
		const value = props.value;
		if (!value) {
			$input.datetimepicker('reset');
			return;
		}

		const formattedValue = moment(value as string).format('MM/DD/YYYY h:mm A');

		if ($input.val() !== formattedValue) {
			$input.val(formattedValue);
		}
	}, [props.value, isDateTime]);

	useEffect(() => {
		if (!isDateOnly || !inputRef.current) return;
		const $input = $(inputRef.current);
		const value = props.value;

		if (!value) {
			$input.datetimepicker('reset');
			return;
		}

		const formattedValue = moment(value as string).format('MM/DD/YYYY');

		if ($input.val() !== formattedValue) $input.val(formattedValue);

	}, [props.value, isDateOnly]);

	const domProps = clone(props);

	if (isDateTime || isDateOnly) {
		domProps.type = 'text';
		delete domProps.value;
	}

	return <input {...domProps} ref={inputRef} />;
};

type SelectProps = Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> & (
{
	chosen?: false,
	onChange: React.SelectHTMLAttributes<HTMLSelectElement>['onChange'],
} | {
	chosen: true | Chosen.Options,
	multiple?: false,
	onChange?: (val: string) => void,
} | {
	chosen: true | Chosen.Options,
	multiple: true,
	onChange?: (val: string[]) => void,
});
export const Select = ({chosen, onChange, ...props}: SelectProps) => {
	const selectRef = useRef<HTMLSelectElement>(null);

	useEffect(() => {
		if (chosen && selectRef.current) {
			const $select = $(selectRef.current);
			$select.chosen(chosen === true ? undefined : chosen);
		}
	}, []);

	useEffect(() => {
		if (chosen && selectRef.current) {
			const $select = $(selectRef.current);
			$select.trigger('chosen:updated');
		}
	}, [props.children, props.value]);

	useEffect(() => {
		if (chosen && selectRef.current) {
			const $select = $(selectRef.current);
			$select.off('change');
			if (onChange) $select.on('change', () => {
				onChange($select.val())
			});
		}
	}, [onChange]);

	return (
		<select {...props} {...chosen ? {onChange: () => null} : {onChange}} ref={selectRef}>
			{props.children}
		</select>
	);
};

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	spinner?: boolean;
	spinnerColor?: BootstrapColor;
	spinnerSize?: BootstrapSize;
}
export const Button = ({spinner, spinnerColor, spinnerSize, ...props}: ButtonProps) => {
	if (spinner) {
		props.disabled = true;
		props.style = {
			...props.style,
			position: 'relative',
			pointerEvents: 'none',
		};
	}

	return (
		<button {...props}>
			{spinner && <BtnSpinner color={spinnerColor} size={spinnerSize} />}
			{props.children}
		</button>
	);
};

export const BtnSpinner = ({color = 'white', size = 'sm'}: {color?: BootstrapColor, size?: BootstrapSize}) => (
	<div className={`spinner-backdrop position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center`}>
		<span className={`spinner-border spinner-border-${size} text-${color}`}></span>
	</div>
);

type DateFilterValue = {
	type: string,
	from?: number | string,
	to?: number | string,
} | undefined
interface DateFilterParams {
	label?: string,
	helpText?: string,
	value: DateFilterValue,
	onChange?: (val: DateFilterValue) => void,
};
export const DateFilter = ({ label, helpText, value, onChange }: DateFilterParams) => {
	const [type, setType] = useState<string | undefined>();
	const [from, setFrom] = useState<number | string | undefined>();
	const [to, setTo] = useState<number | string | undefined>();

	const periodAgoFilters = ['days_ago', 'weeks_ago', 'months_ago', 'years_ago'];
	const timeAgo = type && periodAgoFilters.includes(type) ? {
		days_ago: 'days',
		weeks_ago: 'weeks',
		months_ago: 'months',
		years_ago: 'years',
	}[type] : null;

	useEffect(() => {
		if (!value) {
			reset();
			return;
		}

		setType(value.type);
		setFrom(value.from);
		setTo(value.to);
	}, [value]);

	useEffect(() => {
		if (onChange) onChange(type ? { type, from, to } : undefined);
	}, [type, from, to]);

	const changeType = (value: string) => {
		setType(value);
		setFrom(undefined);
		setTo(undefined);
	};

	const reset = () => {
		setType(undefined);
		setFrom(undefined);
		setTo(undefined);
	};

	return (
		<>
			<div className="row g-3">
				<div className="form-group col-12">
					{label && <label>{label}</label>}
					<Select className="form-control" chosen={{disable_search: true}} value={type || ''} onChange={changeType}>
						<option value="">No filter</option>
						<optgroup label="Date Range">
							<option value="from">After</option>
							<option value="to">Before</option>
							<option value="range">Between</option>
						</optgroup>
						<optgroup label="Current">
							<option value="today">Today</option>
							<option value="this_week">This week</option>
							<option value="this_month">This month</option>
							<option value="this_year">This year</option>
						</optgroup>
						<optgroup label="Previous">
							<option value="yesterday">Yesterday</option>
							<option value="last_week">Last week</option>
							<option value="last_month">Last month</option>
							<option value="last_year">Last year</option>
						</optgroup>
						<optgroup label="Period Ago">
							<option value="days_ago">Days ago</option>
							<option value="weeks_ago">Weeks ago</option>
							<option value="months_ago">Months ago</option>
							<option value="years_ago">Years ago</option>
						</optgroup>
					</Select>
					{helpText && <small className="form-text text-muted">{helpText}</small>}
				</div>
			</div>
			<div className="row g-3">
				{type && ['from', 'range'].includes(type) && (
					<div className="form-group col-12">
						<label>After</label>
						<Input type="text" className="form-control" value={from} datetimepicker onPickerChange={(val) => setFrom(val || undefined)} />
					</div>
				)}
				{type && ['to', 'range'].includes(type) && (
					<div className="form-group col-12">
						<label>Before</label>
						<Input type="text" className="form-control" value={to} datetimepicker onPickerChange={(val) => setTo(val || undefined)} />
					</div>
				)}
				{type && periodAgoFilters.includes(type) && (
					<div className="form-group col-12">
						<div className="input-group">
							<div className="input-group-prepend">
								<span className="input-group-text">Between</span>
							</div>
							<input type="number" min={0} className="form-control" value={from} onChange={(e) => setFrom(+e.target.value)} />
							<div className="input-group-prepend">
								<span className="input-group-text">{timeAgo} ago and</span>
							</div>
							<input type="number" min={0} className="form-control" value={to} onChange={(e) => setTo(+e.target.value)} />
							<div className="input-group-prepend">
								<span className="input-group-text">{timeAgo} ago</span>
							</div>
						</div>
					</div>
				)}
			</div>
		</>
	);
};

export const Password = (props: JSX.IntrinsicElements['input']) => {
	const [showPassword, setShowPassword] = useState(false);

	return (
		<div className="input-group">
			<input type={showPassword ? 'text' : 'password'} className="form-control" {...props} />
			<span className="input-group-text" onClick={() => setShowPassword(!showPassword)}><i className={`fas fa-fw ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i></span>
		</div>
	);
};